import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { Editor } from 'react-draft-wysiwyg';
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  ICourseTutor,
  TCreateCourseTutor,
  TUpdateCourseTutor,
  createCourseTutor,
  updateCourseTutor,
} from '../../../../../../../../store/V2/CourseTutorsSlice';
import { getCoursesState } from '../../../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../../../hooks';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../../components/Inputs';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../../../components/Inputs/admin/UserListDropdown';
import {
  courseTutorTypeOptions,
  TCourseTutorTypeOption,
} from '../../../../../../../../constants';
import { capitalizeFirstLetter } from '../../../../../../../../helper';

export type TState = {
  tutor: TUserSelectOption | null;
  title: string;
  description: EditorState;
  startDate: Moment | null;
  endDate: Moment | null;
  type: TCourseTutorTypeOption | null;
};

interface AddCourseTutorModalProps {
  open: boolean;
  onClose: () => void;
  mode?: 'edit';
  courseTutor?: ICourseTutor;
}

const editorStyles: React.CSSProperties = {
  minHeight: '250px',
  border: '1px solid lightgray',
  borderRadius: '4px',
  padding: '15px',
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
};

const initialState: TState = {
  tutor: null,
  title: '',
  description: EditorState.createEmpty(),
  startDate: null,
  endDate: null,
  type: null,
};

const AddCourseTutorModal: React.FC<AddCourseTutorModalProps> = ({
  open,
  onClose,
  mode,
  courseTutor,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;

  let isSubmitDisabled = !state.tutor || !state.type;
  let isOneToOneCourse = false;

  if (courseData && courseData.isOneToOne) {
    isOneToOneCourse = true;
  }

  if (!isOneToOneCourse) {
    isSubmitDisabled =
      isSubmitDisabled ||
      !state.title.trim().length ||
      !state.description.getCurrentContent().hasText();
  }

  useEffect(() => {
    if (mode === 'edit' && courseTutor) {
      setState((prevState) => {
        const newState: TState = { ...prevState };

        if (courseTutor) {
          if (courseTutor.tutor && courseTutor.tutor._id) {
            const _tutor = courseTutor.tutor;

            let _name = _tutor.firstname;
            let _roleName = '';

            if (_tutor.lastname) {
              _name += ` ${_tutor.lastname}`;
            }

            if (_tutor.role && _tutor.role.name) {
              _roleName = _tutor.role.name;
              _name += ` (${_roleName})`;
            }

            newState.tutor = {
              label: _name,
              value: _tutor._id,
              roleName: _roleName,
            };
          }

          if (courseTutor.title) {
            newState.title = courseTutor.title;
          }

          if (courseTutor.description) {
            const _blocksFromHTML = convertFromHTML(courseTutor.description);
            const _editorState = ContentState.createFromBlockArray(
              _blocksFromHTML.contentBlocks,
              _blocksFromHTML.entityMap
            );

            newState.description = EditorState.createWithContent(_editorState);
          }

          if (courseTutor.startDate) {
            newState.startDate = moment(courseTutor.startDate);
          }

          if (courseTutor.endDate) {
            newState.endDate = moment(courseTutor.endDate);
          }

          if (courseTutor.type) {
            const _matchingType = courseTutorTypeOptions.find(
              (item) => item.value === courseTutor.type
            );

            if (_matchingType) {
              newState.type = _matchingType;
            }
          }
        }

        return newState;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (courseData && state.tutor) {
      if (mode === 'edit' && courseTutor && state.type) {
        setLoading(true);

        const apiData: TUpdateCourseTutor = {
          id: courseTutor._id,
          course: courseData._id,
          type: state.type.value,
        };

        if (state.title.trim().length) {
          apiData.title = state.title.trim();
        }

        if (state.description.getCurrentContent().hasText()) {
          apiData.description = draftToHtml(
            convertToRaw(state.description.getCurrentContent())
          );
        }

        if (state.startDate) {
          apiData.startDate = state.startDate.clone().toISOString();
        }

        if (state.endDate) {
          apiData.endDate = state.endDate.clone().toISOString();
        }

        if (Object.keys(apiData).length > 1) {
          await dispatch(updateCourseTutor(apiData));
        }
        setLoading(false);
      } else if (mode !== 'edit' && state.type) {
        setLoading(true);

        const apiData: TCreateCourseTutor = {
          course: courseData._id,
          tutor: state.tutor.value,
          type: state.type.value,
        };

        if (state.title.trim().length) {
          apiData.title = state.title.trim();
        }

        if (state.description.getCurrentContent().hasText()) {
          apiData.description = draftToHtml(
            convertToRaw(state.description.getCurrentContent())
          );
        }

        if (state.startDate) {
          apiData.startDate = state.startDate.clone().toISOString();
        }

        if (state.endDate) {
          apiData.endDate = state.endDate.clone().toISOString();
        }

        await dispatch(createCourseTutor(apiData));

        setLoading(false);
      }

      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={isLoading ? onClose : undefined}
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit Tutor' : 'Add New Tutor'}
      </DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Box role="presentation">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Tutor *
                </Typography>
              </div>
              <FormControl fullWidth>
                <UserListDropdown
                  userTypes={['tutor']}
                  disabled={mode === 'edit'}
                  selectedItem={state.tutor}
                  handleChange={(event, value) =>
                    updateStateAttributes({
                      tutor: value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Type *
                </Typography>
              </div>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.type}
                  options={courseTutorTypeOptions}
                  getOptionKey={(option) => option.value}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label.toLowerCase())
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  onChange={(event, value) =>
                    updateStateAttributes({
                      type: value,
                    })
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...defaultTextFieldProps}
                      size="small"
                      placeholder="Type"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Title{!isOneToOneCourse ? ' *' : null}
                </Typography>
              </div>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="title"
                  placeholder="Title"
                  value={state.title}
                  onChange={(event) =>
                    updateStateAttributes({
                      title: event.currentTarget.value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Description{!isOneToOneCourse ? ' *' : null}
                </Typography>
              </div>
              <FormControl fullWidth>
                <Editor
                  editorState={state.description}
                  editorStyle={editorStyles}
                  toolbarClassName="toolbarClassName"
                  wrapperClassName="wrapperClassName"
                  editorClassName="editorClassName"
                  placeholder="Enter Here..."
                  onEditorStateChange={(value) =>
                    updateStateAttributes({
                      description: value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Start Date
                </Typography>
              </div>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    disableCloseOnSelect={false}
                    value={state.startDate}
                    inputFormat="YYYY MMM DD"
                    onChange={(value) =>
                      updateStateAttributes({
                        startDate: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="Start Date"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginBottom: '15px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  End Date
                </Typography>
              </div>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    disableCloseOnSelect={false}
                    value={state.endDate}
                    inputFormat="YYYY MMM DD"
                    onChange={(value) =>
                      updateStateAttributes({ endDate: value })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        placeholder="End Date"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              <DateRangeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          loading={isLoading}
        >
          {mode === 'edit' ? 'Save' : 'Add Tutor'}
        </LoadingButton>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourseTutorModal;
